import type { BrandConfig } from '../types';
import whatIsIncluded1 from '@/assets/images/comrade/img01.webp';
import whatIsIncluded2 from '@/assets/images/comrade/img02.webp';
import whatIsIncluded3 from '@/assets/images/comrade/img03.webp';
import howItWorksThumbnail from '@/assets/images/comrade/how-it-works-cropped.jpg';
import customerFeedbackImg from '@/assets/images/customer-feedback.png';

export const comradeConfig: BrandConfig = {
  name: 'comrade',
  brand: 'CWA',

  // Meta
  title: 'Contractors Marketing Assessment | Comrade Digital Marketing',
  favicon: '/comrade-favicon.ico',

  // Header
  header: {
    logo: '/comrade-logo.svg',
    siteName: 'Contractors <strong>Marketing Assessment</strong>',
    phone: '(872) 242 1074',
    ctaText: 'Request Consultation',
    ctaLink: '/request-consultation',
  },

  // Footer
  footer: {
    logo: '/comrade-logo-white.svg',
    companyName: 'Comrade Digital Marketing',
    tagline: 'Ready to Grow?',
    subtagline:
      'Let’s build your strategy and keep your crews busy year-round.',
    phone: '(872) 242 1074',
    email: 'info@comradeweb.com',
    address: '332 S Michigan Ave, Suite 900, Chicago, Illinois, 60604',
    addressUrl: 'https://maps.app.goo.gl/7ZMXSwHEp8wLjEvC7',
    directionsUrl: 'https://maps.app.goo.gl/dXnBtYcux2xx7sAMA',
    disclaimer: '*This Company does not provide contracting services.',
    copyright: `© ${new Date().getFullYear()} Comrade Digital Marketing`,
    columns: [
      {
        title: 'Services',
        links: [
          {
            label: 'Website Design',
            url: 'https://comradeweb.com/web-design/',
          },
          {
            label: 'SEO Services',
            url: 'https://comradeweb.com/seo/',
          },
          { label: 'PPC Advertising', url: 'https://comradeweb.com/ppc/' },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: 'Blog', url: 'https://comradeweb.com/blog/' },
          { label: 'Results', url: 'https://comradeweb.com/portfolio/' },
        ],
      },
    ],
    bottomLinks: [
      {
        label: 'Privacy Policy',
        url: 'https://comradeweb.com/privacy-policy/',
      },
      { label: 'Terms of Use', url: 'https://comradeweb.com/terms-of-use/' },
    ],
    social: [
      { icon: 'facebook', url: 'https://www.facebook.com/comradeweb' },
      {
        icon: 'linkedin',
        url: 'https://www.linkedin.com/company/comrade-web-studios',
      },
      { icon: 'instagram', url: 'https://www.instagram.com/comradeweb/' },
      { icon: 'youtube', url: 'https://www.youtube.com/@comradeweb' },
    ],
  },

  // Landing sections — same order as GLF for now
  landing: {
    step1Sections: [
      { component: 'CheckPerformance' },
      {
        component: 'CompareYourFirmForm',
        props: { autocompleteId: 'map1', id: 'compare-form-top' },
      },
      { component: 'HowItWorks' },
      { component: 'WhatIsIncluded' },
      { component: 'CustomerFeedback' },
      {
        component: 'CompareYourFirmForm',
        props: {
          autocompleteId: 'map2',
          id: 'compare-form',
          title:
            "Your Competitors Are Already Optimizing Their Marketing. Don't Fall Behind.",
        },
      },
    ],
    step2Sections: [
      { component: 'ContactInformation' },
      { component: 'GrowthFactorsList' },
    ],
  },

  // Hero section
  hero: {
    title: 'Where Are You Losing Jobs Online?',
    badge: 'FREE',
    description:
      "Find Out in 2 Minutes with a Marketing Assessment. You'll get a real-time analysis of your online performance compared to your top competitors.",
  },

  // HowItWorks section
  howItWorks: {
    title: 'Why Should I Use This Marketing Assessment Tool?',
    paragraphs: [
      '<strong>97% of homeowners look for a contractor online.</strong>',
      'Your competitors are already optimizing to get those jobs. Our Marketing Assessment reveals your visibility, website performance, and authority in real time.',
    ],
    videoLink: 'https://www.youtube.com/shorts/RCLdqQk5X8k',
    thumbnail: howItWorksThumbnail,
  },

  // WhatIsIncluded section
  whatIsIncluded: {
    subtitle: '',
    title: "What's Included in My Marketing Assessment Report?",
    buttonText: 'Compare your company',
    features: [
      {
        title: 'Check your rankings for top keywords',
        description:
          'How often and how prominently your company appears in local search results for high-intent service keywords, and which competitors rank above you.',
        image: whatIsIncluded1,
      },
      {
        title: 'Assess what your clients say about you',
        description:
          'A breakdown of your total reviews, average rating, and competitive review gap, showing whether your reputation is winning or losing you jobs.',
        image: whatIsIncluded2,
      },
      {
        title: 'See how well your website converts prospects',
        description:
          "An evaluation of your site's speed, mobile usability, security, and technical structure, all of which affect rankings and whether visitors actually contact you.",
        image: whatIsIncluded3,
      },
    ],
  },

  // Customer feedback section
  customerFeedback: {
    title: 'Customer feedback',
    quote:
      "The Marketing Assessment tool provided instant clarity on our marketing success, something I never found in detailed reports. It's the simplest and quickest way to understand our marketing performance.",
    author: 'SATISFIED CUSTOMER, Business Owner',
    rating: 5,
    image: customerFeedbackImg,
  },

  // Form
  form: {
    businessLabel: 'Enter your business name',
    businessTooltip: 'Start typing the name of your business',
    practiceLabel: 'Primary service',
    practiceTooltip:
      'Select your service type to compare against main competitors.',
    buttonText: 'Get My Report',
    searchSuffix: 'contractor',
    searchExample: 'near me',
    bottomTitle:
      'Get a clear picture of your online performance and actionable ideas for improving your online presence',
    roles: ['Owner / Co-owner', 'General Manager', 'Office Manager', 'Other'],
    services: [
      'Cabinets',
      'Cleaning',
      'Concrete / Masonry',
      'Deck',
      'Electrician',
      'Fence',
      'Flooring',
      'Garage Door',
      'Home Builders',
      'Home Improvement',
      'Home Services',
      'HVAC',
      'Interior Design',
      'Landscaping',
      'Moving',
      'Painting',
      'Pest Control',
      'Plumbing',
      'Pool and Spa',
      'Remodeling',
      'Restoration',
      'Roofing',
      'Siding',
      'Solar',
      'Stone & Countertops',
      'Tile',
      'Windows & Doors',
      'Other',
    ],
  },

  // Consultation banner
  consultation: {
    title: 'Request Consultation',
    description:
      "Schedule a complimentary call to discuss your business's most pressing issues and marketing goals.",
    buttonText: 'Request Consultation',
    pageTitle:
      "Schedule a complimentary call to discuss your business's most pressing issues and marketing goals.",
  },

  // Education / checklist section
  education: {
    sectionTitle:
      'Boost Your Rankings and Get More Customers with These Actionable Steps:',
    reputationGoodText:
      'Your business has done good job collection reviews, but there are competitors whose average rating is higher than yours. Try ' +
      'to increase your average rating to 5 stars. This not only boosts your online reputation but also positions ' +
      'you among the top 10 local listings, notably on platforms like Google Maps.',
    reputationGreatText:
      "Your business's average rating is exceptional, while there are competitors who have more reviews. Aim " +
      'to add {count} + new positive reviews on Google. This not only boosts your online reputation but also ' +
      'positions you among the top 10 local listings, notably on platforms like Google Maps.',
    showcaseAchievementsText:
      'Showcase achievements: Display notable accolades such as industry certifications, professional recognitions, etc.',
  },

  // Dashboard session banner
  sessionBanner: {
    title: 'Book A Free Strategy Session <span>with our Team</span>',
    text: '<strong>Looking to grow your business? Need more customers?</strong> Our team of experts will work closely with you to build a customized marketing strategy tailored to your business',
    items: ['Reach new customers', 'Boost sales', 'Hit your financial goals'],
  },

  // HubSpot
  hubspot: {
    formId: '3a05a4b2-de70-4794-b8c9-336905fa79c7',
    portalId: '471763',
    // CDM keeps the shared `company` property — only role/url are brand-split.
    companyNameField: 'company',
    companyUrlField: 'website',
    roleField: 'company_role',
    practiceAreaField: 'types_of_trades',
    includeDomain: true,
  },

  // HubSpot Meetings
  meeting: {
    url: 'https://meetings.hubspot.com/mary-grinnan/meet-with-comrade-digital-marketing-expert',
  },

  // PDF report texts
  pdf: {
    heroTitle: 'Discover hidden opportunities to grow your business',
    nextStepsTitle:
      'Your next customer is searching for a contractor right now',
    heroDescription:
      "In this report, you will see how your business's marketing stacks up to your top competitors and how you can improve your marketing performance and get more leads and new customers.",
    practiceNoun: 'contractor',
    ctaTitle:
      'Get a growth plan custom-tailored for your business - <br /> a $499 value—completely free!',
    nextStepsAuthorTitle: 'Business Growth Expert',
    nextStepsBrandTitle: 'Brought to you by Comrade Digital Marketing',
    nextStepsBrandDescription:
      'Comrade Digital Marketing helps businesses achieve rapid and sustained growth through SEO, PPC and website design services high-performance web design services.',
    reviewsIntroHeading:
      '84% of surveyed business owners indicated that increasing their revenue is their top priority.',
    reviewsBodyLines: [
      'If revenue is your top priority as well, you know the only way to achieve this is by either increasing your customer base or raising prices (not always a great option), or some combination of the two.',
      'Increasing the number of customers your business takes on is the most reliable way to grow, but most businesses struggle with actually making this happen.',
      "To get more customers, you need to attract more clients. <strong>While 59% of all customers sought a referral to a contractor, 57% searched online, and 16% did both.</strong><sup>2</sup> Given these statistics, it's clear your online presence should be a major customer generator for your business.",
      '<strong>The following report, created specifically for your business, reveals</strong>',
    ],
    reviewsBulletLines: [
      'The number of searches in your geographical market for contractors in your service area',
      'How your website performs at attracting demand, expressed as search traffic',
      'Untapped opportunities to get more customers',
    ],
    tocItems: [
      {
        title: 'Overall Score',
        description:
          'See the aggregate score for your website and web presence',
      },
      {
        title: 'Web Presence',
        description:
          'See if prospects are more likely to find your business or those of your competitors',
      },
      {
        title: 'Business Reputation',
        description:
          'Do the number and quality of reviews put your business ahead of the competition?',
      },
      {
        title: 'Website Performance',
        description:
          "Does your website's technical performance get in the way of getting more customers?",
      },
      {
        title: 'Website Authority',
        description:
          'The number and quality of backlinks indicate whether your site is thrust-worthy. See how your website stacks up against the competition.',
      },
      {
        title: 'Next Steps',
        description:
          'Steps to take now to grow the number of customers you attract.',
      },
    ],
  },
};
